import { useModal } from '@/features/Modal/Modal.hooks'
import { DrawerModalListItem } from '@/features/Modal/Modal.types'
import { DrawerModalList } from '@/features/Modal/ui/DrawerModalList/DrawerModalList'
import { message } from 'antd'
import React, { useCallback } from 'react'
import { useCopyToClipboard } from 'usehooks-ts'

type Props = {
  publicKey: string;
}

export function WalletProfileActionsModal({ publicKey }: Props) {
  const { destroyModal } = useModal()
  const [_, copy] = useCopyToClipboard()

  const handleCopy = useCallback(async () => {
    await copy(publicKey)
    message.success({ content: 'Wallet address successfully copied', icon: '🎉 ' })
    destroyModal()
  }, [destroyModal, copy, publicKey])

  const walletProfileList: DrawerModalListItem[] = [
    {
      text: 'Copy address',
      visible: true,
      onClick: handleCopy,
      icon: (
        <svg width="18" height="23" viewBox="0 0 18 23" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path
            d="M4.46777 5.69629V3.6709C4.46777 2.77441 4.69466 2.09652 5.14844 1.63721C5.60221 1.17236 6.27458 0.939941 7.16553 0.939941H10.2866C10.7681 0.939941 11.1969 1.00911 11.5732 1.14746C11.9551 1.28027 12.3009 1.50716 12.6108 1.82812L16.7446 6.03662C17.0711 6.37419 17.3008 6.73389 17.4336 7.11572C17.5664 7.49202 17.6328 7.9541 17.6328 8.50195V15.0762C17.6328 15.9727 17.4032 16.6506 16.9438 17.1099C16.4901 17.5692 15.8205 17.7988 14.9351 17.7988H13.2334V16.1719H14.7856C15.1896 16.1719 15.4912 16.0695 15.6904 15.8647C15.8952 15.6545 15.9976 15.3584 15.9976 14.9766V8.10352H12.3452C11.8306 8.10352 11.4404 7.97347 11.1748 7.71338C10.9092 7.44775 10.7764 7.05762 10.7764 6.54297V2.5752H7.29834C6.8999 2.5752 6.59831 2.67757 6.39355 2.88232C6.19434 3.08708 6.09473 3.38314 6.09473 3.77051V5.69629H4.46777ZM12.1377 6.33545C12.1377 6.47933 12.1681 6.58447 12.229 6.65088C12.2954 6.71175 12.3978 6.74219 12.5361 6.74219H15.5742L12.1377 3.25586V6.33545ZM0.358887 19.3345V7.9292C0.358887 7.03271 0.585775 6.35482 1.03955 5.89551C1.49333 5.43066 2.16569 5.19824 3.05664 5.19824H5.94531C6.44889 5.19824 6.86393 5.25358 7.19043 5.36426C7.52246 5.4694 7.85449 5.69352 8.18652 6.03662L12.6855 10.6021C12.918 10.84 13.0951 11.0669 13.2168 11.2827C13.3385 11.4985 13.4188 11.7393 13.4575 12.0049C13.5018 12.265 13.5239 12.5804 13.5239 12.9512V19.3345C13.5239 20.231 13.297 20.9089 12.8433 21.3682C12.3895 21.8275 11.7171 22.0571 10.8262 22.0571H3.05664C2.16569 22.0571 1.49333 21.8275 1.03955 21.3682C0.585775 20.9144 0.358887 20.2365 0.358887 19.3345ZM1.99414 19.2349C1.99414 19.6222 2.09375 19.9183 2.29297 20.123C2.49219 20.3278 2.79102 20.4302 3.18945 20.4302H10.6851C11.0835 20.4302 11.3823 20.3278 11.5815 20.123C11.7863 19.9183 11.8887 19.6222 11.8887 19.2349V13.1421H7.50586C6.91927 13.1421 6.47933 12.9982 6.18604 12.7104C5.89274 12.4172 5.74609 11.9717 5.74609 11.374V6.8335H3.19775C2.79378 6.8335 2.49219 6.93587 2.29297 7.14062C2.09375 7.34538 1.99414 7.63867 1.99414 8.02051V19.2349ZM7.67188 11.7061H11.6646L7.18213 7.14893V11.2163C7.18213 11.3879 7.22087 11.5124 7.29834 11.5898C7.37581 11.6673 7.50033 11.7061 7.67188 11.7061Z"
            fill="var(--textSecondary)"
          />
        </svg>
      ),
    },
    {
      text: 'Rename',
      visible: true,
      isLink: true,
      link: `wallets/${publicKey}/rename?from=main`,
      icon: (
        <svg width="14" height="15" viewBox="0 0 14 15" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path
            d="M2.52588 13.6162L0.51709 14.3882C0.395345 14.4325 0.284668 14.4048 0.185059 14.3052C0.0909831 14.2056 0.0660807 14.0921 0.110352 13.9648L0.923828 12.0142L10.2622 2.68408L11.856 4.28613L2.52588 13.6162ZM12.6528 3.50586L11.0425 1.90381L11.939 1.01562C12.1603 0.799805 12.39 0.683594 12.6279 0.666992C12.8659 0.650391 13.0872 0.741699 13.292 0.940918L13.6157 1.27295C13.8205 1.4777 13.9173 1.69629 13.9062 1.92871C13.8952 2.16113 13.7762 2.39079 13.5493 2.61768L12.6528 3.50586Z"
            fill="var(--textSecondary)"
          />
        </svg>
      ),
    },
  ]

  return <DrawerModalList size={160} list={walletProfileList} />
}
